import type { ProgrammeItem, Screen } from '../api/client';

/** "4s ago", "2m ago", or "never". */
export function heartbeatAge(lastSeenAt: string | undefined, now: number): string {
  if (!lastSeenAt) return 'never';
  const seconds = Math.max(0, Math.round((now - Date.parse(lastSeenAt)) / 1000));
  if (seconds < 120) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  return minutes < 120 ? `${minutes}m ago` : `${Math.round(minutes / 60)}h ago`;
}

/** "21:00" in the venue's time zone. */
export function venueTime(iso: string | undefined, timezone: string | undefined): string {
  if (!iso) return '';
  return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: timezone }).format(new Date(iso));
}

export type CardState = 'unpaired' | 'offline' | 'starting' | 'idle' | 'playing' | 'promo';

/** What the screen is actually doing, as reported by its player. Offline wins. */
export function cardState(screen: Screen): CardState {
  if (screen.status === 'unpaired') return 'unpaired';
  if (screen.status === 'offline') return 'offline';
  switch (screen.device?.reportedState) {
    case 'playing':
      return 'playing';
    case 'promo':
      return 'promo';
    case 'idle':
      return 'idle';
    default:
      return 'starting';
  }
}

/** Headline for a screen card: the fixture, the loop, the channel, or the state. */
export function screenHeadline(screen: Screen): { title: string; detail: string } {
  if (screen.status === 'unpaired') return { title: 'No player paired yet', detail: 'Start the TV and pair it with the code it shows' };
  if (screen.promo) return { title: screen.promo.title, detail: 'Nectar promo slate on screen' };
  const np = screen.nowPlaying;
  if (np) {
    if (np.kind === 'loop') return { title: `${np.title}${np.detail ? ` · ${np.detail.toLowerCase()}` : ''}`, detail: 'Sponsor loop' };
    if (np.kind === 'channel') return { title: np.title, detail: np.detail ?? 'Channel' };
    return { title: np.title, detail: [np.competition, np.detail].filter(Boolean).join(' · ') };
  }
  if (screen.channel) return { title: screen.channel.name, detail: 'Live feed' };
  return { title: 'Ambient screen', detail: 'Venue branding and QR code' };
}

/** "SAH 2 – 1 MAR" or the plain score. */
export function scoreline(item: ProgrammeItem | undefined): string | undefined {
  if (!item?.score) return undefined;
  return item.homeShort && item.awayShort ? `${item.homeShort} ${item.score} ${item.awayShort}` : item.score;
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}
